import React, { useState, useReducer } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCircleIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  CalendarDaysIcon,
  PlusIcon,
  StarIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: INITIAL MOCK DATA ---
const initialEmployee = {
  name: 'Aditya Verma',
  jobTitle: 'Lead Software Engineer',
  department: 'Technology',
  manager: 'Sanjay Kumar',
  rating: 'Exceeds Expectations',
  lastReviewDate: '2025-09-05',
};

const initialReviews = [
  {
    id: 1,
    date: '2025-09-05',
    rating: 'Exceeds Expectations',
    summary: 'Demonstrated exceptional leadership on Project Falcon, consistently delivered high-quality code, and mentored junior team members effectively.',
    details: {
      roleSkills: 'Exceptional (5/5)',
      communication: 'Strong (4/5)',
      collaboration: 'Strong (4/5)',
      futureGoals: ['Lead migration to new microservices architecture.', 'Mentor two interns next quarter.'],
    },
  },
  {
    id: 2,
    date: '2025-03-10',
    rating: 'Meets Expectations',
    summary: 'Consistently met project deadlines and contributed valuable insights during sprint planning. Room for growth in mentoring and cross-team collaboration.',
    details: {
      roleSkills: 'Meets Expectations (3/5)',
      communication: 'Meets Expectations (3/5)',
      collaboration: 'Meets Expectations (3/5)',
      futureGoals: ['Improve documentation for internal APIs.', 'Complete online certification in cloud technologies.'],
    },
  },
];

const initialGoals = [
  { id: 1, title: 'Complete Q3 Project Report', dueDate: '2025-10-31', status: 'In Progress', progress: 75 },
  { id: 2, title: 'Learn Advanced SQL Queries', dueDate: '2025-11-15', status: 'Not Started', progress: 0 },
  { id: 3, title: 'Lead migration to new architecture', dueDate: '2025-12-31', status: 'In Progress', progress: 20 },
  { id: 4, title: 'Onboard 5 New Clients', dueDate: '2025-08-30', status: 'Completed', progress: 100 },
];

const mockFeedback = {
  manager: { rating: '5', summary: 'Aditya is a top performer. Always goes above and beyond.' },
  peers: { rating: '4.5', summary: 'A great collaborator, always willing to help. Very strong technically.' },
  self: { rating: '4', summary: 'I feel I have grown a lot and contributed significantly to my team\'s success.' },
};
// --- END: INITIAL MOCK DATA ---

// Reducer for managing reviews
const reviewsReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_REVIEW':
      return [
        { ...action.payload, id: state.length > 0 ? Math.max(...state.map(r => r.id)) + 1 : 1 },
        ...state,
      ];
    default:
      return state;
  }
};

// Reducer for managing goals
const goalsReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_GOAL':
      return [
        { ...action.payload, id: state.length > 0 ? Math.max(...state.map(g => g.id)) + 1 : 1 },
        ...state,
      ];
    case 'UPDATE_GOAL':
      return state.map(goal =>
        goal.id === action.payload.id ? { ...goal, ...action.payload.updates } : goal
      );
    default:
      return state;
  }
};

const EmployeeProfile = () => {
  const [reviews, dispatchReviews] = useReducer(reviewsReducer, initialReviews);
  const [goals, dispatchGoals] = useReducer(goalsReducer, initialGoals);
  const [expandedReviewId, setExpandedReviewId] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [employee, setEmployee] = useState(initialEmployee);

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'Outstanding':
      case 'Exceptional':
      case 'Completed':
        return 'bg-emerald-100 text-emerald-700';
      case 'Exceeds Expectations':
      case 'Strong':
      case 'In Progress':
        return 'bg-sky-100 text-sky-700';
      case 'Meets Expectations':
      case 'Average':
      case 'Not Started':
        return 'bg-amber-100 text-amber-700';
      case 'Needs Improvement':
      case 'Below Average':
        return 'bg-rose-100 text-rose-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const completedGoalsCount = goals.filter(g => g.status === 'Completed').length;

  const handleNewReview = (newReview) => {
    dispatchReviews({ type: 'ADD_REVIEW', payload: newReview });
    setEmployee({ ...employee, rating: newReview.rating, lastReviewDate: newReview.date });
    setShowReviewForm(false);
  };

  const handleNewGoal = (newGoal) => {
    dispatchGoals({ type: 'ADD_GOAL', payload: newGoal });
    setShowGoalForm(false);
  };
  
  const handleProgressChange = (goalId, newProgress) => {
    const newStatus = newProgress === 100 ? 'Completed' : newProgress > 0 ? 'In Progress' : 'Not Started';
    dispatchGoals({ type: 'UPDATE_GOAL', payload: { id: goalId, updates: { progress: newProgress, status: newStatus } } });
  };
  

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-slate-200 p-8 text-gray-800 font-sans">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <div className="flex items-center space-x-6">
          <UserCircleIcon className="h-24 w-24 text-slate-500" />
          <div>
            <h1 className="text-4xl font-extrabold text-slate-900">{employee.name}</h1>
            <p className="text-xl font-light text-slate-600 mt-1">{employee.jobTitle} - {employee.department}</p>
            <p className="text-sm text-gray-500 mt-2">Manager: {employee.manager}</p>
          </div>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 max-w-7xl mx-auto">
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 flex items-center space-x-4">
            <div className="p-3 rounded-xl bg-slate-100">
              <ChartBarIcon className="h-8 w-8 text-slate-600" />
            </div>
            <div>
              <p className="text-sm font-light text-gray-500">Current Rating</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{employee.rating}</h3>
            </div>
          </div>
        </motion.div>
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 flex items-center space-x-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <ClipboardDocumentCheckIcon className="h-8 w-8 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-light text-gray-500">Completed Goals</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{completedGoalsCount}</h3>
            </div>
          </div>
        </motion.div>
        <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 flex items-center space-x-4">
            <div className="p-3 rounded-xl bg-cyan-100">
              <CalendarDaysIcon className="h-8 w-8 text-cyan-600" />
            </div>
            <div>
              <p className="text-sm font-light text-gray-500">Last Review Date</p>
              <h3 className="text-xl font-bold text-gray-900 mt-1">{moment(employee.lastReviewDate).format('MMM D, YYYY')}</h3>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
        {/* Performance Review History */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 h-full flex flex-col"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900">Review History</h3>
            <button
              onClick={() => setShowReviewForm(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              <span className="text-sm">Add Review</span>
            </button>
          </div>
          <div className="space-y-4">
            <AnimatePresence>
              {reviews.map((review) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="border border-gray-200 rounded-xl p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedReviewId(expandedReviewId === review.id ? null : review.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-lg text-gray-900">Review ({moment(review.date).format('MMM YYYY')})</h4>
                      <p className="text-sm text-gray-500 mt-1 truncate">{review.summary}</p>
                    </div>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-gray-400 transform transition-transform ${expandedReviewId === review.id ? 'rotate-180' : 'rotate-0'}`}
                    />
                  </div>
                  <AnimatePresence>
                    {expandedReviewId === review.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 pt-4 border-t border-gray-200"
                      >
                        <p className="text-sm font-semibold mb-2">Detailed Ratings:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                          <li>Role-Specific Skills: {review.details.roleSkills}</li>
                          <li>Communication: {review.details.communication}</li>
                          <li>Collaboration: {review.details.collaboration}</li>
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Goals and 360 Feedback */}
        <div className="space-y-6">
          {/* Goals & KPIs */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.4 }}
            className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 h-full flex flex-col"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Goals & KPIs</h3>
              <button
                onClick={() => setShowGoalForm(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                <span className="text-sm">Set Goal</span>
              </button>
            </div>
            <div className="space-y-4">
              {goals.map(goal => (
                <div key={goal.id} className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">{goal.title}</p>
                      <span className={`mt-1 inline-flex text-xs leading-5 font-semibold rounded-full px-2 ${getRatingColor(goal.status)}`}>
                        {goal.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{moment(goal.dueDate).format('MMM D, YYYY')}</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-3">
                    <motion.div
                      className={`${getRatingColor(goal.status).split(' ')[0].replace('bg', 'bg-')}-700 h-2.5 rounded-full`}
                      initial={{ width: 0 }}
                      animate={{ width: `${goal.progress}%` }}
                      transition={{ duration: 0.5 }}
                    ></motion.div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={goal.progress}
                    onChange={(e) => handleProgressChange(goal.id, Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <span className="text-xs text-gray-500 mt-1">{goal.progress}% progress</span>
                </div>
              ))}
            </div>
          </motion.div>
          
          {/* 360 Feedback Summary */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.5 }}
            className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 h-full flex flex-col"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">360-Degree Feedback</h3>
            <div className="space-y-4">
              <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                <p className="font-semibold">Manager Feedback: <span className={`font-bold text-lg ${getRatingColor(mockFeedback.manager.rating)}`}>{mockFeedback.manager.rating}/5</span></p>
                <p className="text-sm text-gray-500 mt-1">{mockFeedback.manager.summary}</p>
              </div>
              <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                <p className="font-semibold">Peer Feedback: <span className={`font-bold text-lg ${getRatingColor(mockFeedback.peers.rating)}`}>{mockFeedback.peers.rating}/5</span></p>
                <p className="text-sm text-gray-500 mt-1">{mockFeedback.peers.summary}</p>
              </div>
              <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                <p className="font-semibold">Self-Assessment: <span className={`font-bold text-lg ${getRatingColor(mockFeedback.self.rating)}`}>{mockFeedback.self.rating}/5</span></p>
                <p className="text-sm text-gray-500 mt-1">{mockFeedback.self.summary}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Forms as Modals */}
      <AnimatePresence>
        {showReviewForm && (
          <ReviewForm onAddReview={handleNewReview} onCancel={() => setShowReviewForm(false)} />
        )}
        {showGoalForm && (
          <GoalForm onAddGoal={handleNewGoal} onCancel={() => setShowGoalForm(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

// New Review Form Component
const ReviewForm = ({ onAddReview, onCancel }) => {
  const [formData, setFormData] = useState({
    date: '',
    rating: '',
    summary: '',
    details: {
      roleSkills: '',
      communication: '',
      collaboration: '',
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name in formData.details) {
      setFormData(prev => ({
        ...prev,
        details: {
          ...prev.details,
          [name]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAddReview(formData);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 p-4"
    >
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-lg"
      >
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
          <StarIcon className="h-5 w-5 text-yellow-500" />
          <span>Add Performance Review</span>
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Overall Rating</label>
            <select
              name="rating"
              value={formData.rating}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 sm:text-sm"
              required
            >
              <option value="">Select a rating</option>
              <option value="Exceptional">Exceptional</option>
              <option value="Exceeds Expectations">Exceeds Expectations</option>
              <option value="Meets Expectations">Meets Expectations</option>
              <option value="Needs Improvement">Needs Improvement</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Summary</label>
            <textarea
              name="summary"
              rows="2"
              value={formData.summary}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 sm:text-sm"
              required
            ></textarea>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Skills</label>
              <input type="text" name="roleSkills" placeholder="e.g. Exceptional (5/5)" value={formData.details.roleSkills} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Communication</label>
              <input type="text" name="communication" placeholder="e.g. Strong (4/5)" value={formData.details.communication} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Collaboration</label>
              <input type="text" name="collaboration" placeholder="e.g. Meets Expectations (3/5)" value={formData.details.collaboration} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm" />
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-slate-800 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-slate-700"
            >
              Save Review
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// New Goal Form Component
const GoalForm = ({ onAddGoal, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    dueDate: '',
    status: 'Not Started',
    progress: 0,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onAddGoal(formData);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 p-4"
    >
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-lg"
      >
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
          <TrophyIcon className="h-5 w-5 text-green-500" />
          <span>Set New Goal</span>
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Goal Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Due Date</label>
            <input
              type="date"
              name="dueDate"
              value={formData.dueDate}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500 sm:text-sm"
              required
            />
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-slate-800 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-slate-700"
            >
              Save Goal
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default EmployeeProfile;